import React, {useEffect} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useForm} from 'react-hook-form';
import {Link} from 'react-router-dom';
import {apiClient} from '@common/http/query-client';
import {useAuth} from '@common/auth/use-auth';
import {FileUploadProvider} from '@common/uploads/uploader/file-upload-provider';
import {FormFileEntryField} from '@common/uploads/components/file-entry-field';
import {Form} from '@ui/forms/form';
import {FormSelect} from '@ui/forms/select/select';
import {Item} from '@ui/forms/listbox/item';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {toast} from '@ui/toast/toast';
import {Trans} from '@ui/i18n/trans';
import {BusinessIcon} from '@common/icons/material/Business';
import {CloudUploadIcon} from '@common/icons/material/CloudUpload';
import {CheckCircleIcon} from '@common/icons/material/CheckCircle';

type CompanyType = 'label' | 'aggregator' | 'distributor';
type PartnerStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'suspended'
  | 'validation_required'
  | 'blocked';

interface PartnerRegistrationForm {
  company_name: string;
  trade_name?: string;
  company_type: CompanyType;
  tax_id?: string;
  country?: string;
  address?: {
    street?: string;
    number?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
  responsible_name: string;
  corporate_email: string;
  phone?: string;
  website?: string;
  approximate_artists?: number;
  approximate_catalog?: number;
  social_links?: {
    instagram?: string;
    youtube?: string;
    spotify?: string;
    linkedin?: string;
  };
  validation_document_url?: string;
  contract_url?: string;
  operation_description?: string;
  future_bank_info?: {
    bank?: {
      bank_name?: string;
      bank_code?: string;
      branch_number?: string;
      branch_digit?: string;
      account_number?: string;
      account_digit?: string;
      account_type?: 'checking' | 'savings' | 'payment' | 'salary';
      beneficiary_name?: string;
      holder_tax_id?: string;
    };
    pix?: {
      key_type?: 'cpf' | 'cnpj' | 'email' | 'phone' | 'random';
      key?: string;
      holder_name?: string;
      bank_name?: string;
    };
    payout?: {
      preferred_method?: 'pix' | 'ted';
    };
    notes?: string;
  };
  terms_accepted: boolean;
  username?: string;
  password?: string;
}

interface ExistingPartnerAccount extends Omit<PartnerRegistrationForm, 'terms_accepted' | 'password' | 'username'> {
  id: number;
  status: PartnerStatus;
  revision_notes?: string | null;
  documents?: Array<{type: string; file_url?: string | null}>;
}

export function RegisterPartnerAccountGadPage() {
  const {user} = useAuth();
  const queryClient = useQueryClient();
  const form = useForm<PartnerRegistrationForm>({
    defaultValues: emptyRegistrationForm(),
  });

  const accountQuery = useQuery({
    queryKey: ['gad', 'me'],
    enabled: !!user,
    queryFn: () =>
      apiClient
        .get<{partner_account: ExistingPartnerAccount | null}>('gad/me')
        .then(r => r.data.partner_account),
  });

  const existingAccount = accountQuery.data;
  const canRevise = !!existingAccount && ['rejected', 'validation_required'].includes(existingAccount.status);
  const formLocked = (!!user && accountQuery.isLoading) ||
    (!!existingAccount && !canRevise && existingAccount.status !== 'approved');

  useEffect(() => {
    if (!existingAccount) return;
    form.reset(formValuesFromAccount(existingAccount));
  }, [existingAccount, form]);

  const createAccount = useMutation({
    mutationFn: (payload: PartnerRegistrationForm) =>
      apiClient.post('gad/partner-accounts', payload).then(r => r.data),
    onSuccess: () => {
      toast.positive(existingAccount ? 'Cadastro atualizado e reenviado para análise.' : 'Cadastro enviado para análise.');
      queryClient.invalidateQueries({queryKey: ['gad']});
    },
  });

  return (
    <FileUploadProvider>
      <main className="min-h-screen bg-alt">
        <div className="mx-auto max-w-1280 px-24 py-32 md:py-48">
          <div className="mb-32 flex flex-wrap items-center justify-between gap-16">
            <div>
              <div className="mb-8 flex items-center gap-10 text-sm font-semibold uppercase text-primary">
                <BusinessIcon size="sm" />
                <span>Ecossistema GAD</span>
              </div>
              <h1 className="text-3xl font-semibold text-main md:text-4xl">
                Cadastro de Gravadoras, Agregadoras e Distribuidoras
              </h1>
              <p className="mt-10 max-w-720 text-sm text-muted md:text-base">
                Envie os dados comerciais, documentais e operacionais para
                aprovação manual antes da liberação das APIs Sandbox e Produção.
              </p>
            </div>
            <Button
              elementType={Link}
              to="/dashboard-partner-gad"
              variant="outline"
              color="primary"
            >
              Ver painel
            </Button>
          </div>

          <Form
            form={form}
            onSubmit={values => {
              if (!formLocked) createAccount.mutate(values);
            }}
            className="space-y-24"
          >
            {existingAccount && (
              <div className={`rounded border p-18 ${formLocked ? 'border-warning bg-warning/10' : 'border-primary bg-primary/10'}`}>
                <div className="text-sm font-semibold uppercase">{statusLabel(existingAccount.status)}</div>
                <p className="mt-8 text-sm text-muted">
                  {formLocked
                    ? 'Este cadastro já foi enviado e está bloqueado para edição neste status.'
                    : 'Revise os dados solicitados e reenvie o cadastro para nova análise.'}
                </p>
                {existingAccount.revision_notes && (
                  <div className="mt-12 rounded border bg-paper p-12 text-sm">
                    <div className="mb-4 font-semibold">Mensagem de revisão</div>
                    <div className="text-muted">{existingAccount.revision_notes}</div>
                  </div>
                )}
              </div>
            )}

            <fieldset disabled={formLocked} className={formLocked ? 'pointer-events-none opacity-60' : undefined}>
              <div className="space-y-24">
                <Section title="Empresa">
                  <Grid>
                    <FormTextField name="company_name" label="Nome da empresa" required />
                    <FormTextField name="trade_name" label="Nome fantasia" />
                    <FormSelect
                      name="company_type"
                      selectionMode="single"
                      label="Tipo da empresa"
                    >
                      <Item value="label">Gravadora</Item>
                      <Item value="aggregator">Agregadora</Item>
                      <Item value="distributor">Distribuidora</Item>
                    </FormSelect>
                    <FormTextField name="tax_id" label="CNPJ / Tax ID" />
                    <FormTextField name="country" label="País" />
                    <FormTextField name="website" label="Website" type="url" />
                  </Grid>
                </Section>

                <Section title="Endereço e responsável">
                  <Grid>
                    <FormTextField name="address.street" label="Endereço" />
                    <FormTextField name="address.number" label="Número / complemento" />
                    <FormTextField name="address.city" label="Cidade" />
                    <FormTextField name="address.region" label="Estado / região" />
                    <FormTextField name="address.postal_code" label="CEP / código postal" />
                    <FormTextField name="address.country" label="País do endereço" />
                    <FormTextField name="responsible_name" label="Responsável legal" required />
                    <FormTextField name="corporate_email" label="Email corporativo" type="email" required />
                    <FormTextField name="phone" label="Telefone" />
                  </Grid>
                </Section>

                {!user && (
                  <Section title="Acesso">
                <Grid>
                  <FormTextField name="username" label="Usuário" />
                  <FormTextField name="password" label="Senha" type="password" required />
                </Grid>
                  </Section>
                )}

                <Section title="Catálogo e operação">
              <Grid>
                <FormTextField
                  name="approximate_artists"
                  label="Quantidade aproximada de artistas"
                  type="number"
                  min={0}
                />
                <FormTextField
                  name="approximate_catalog"
                  label="Quantidade aproximada de catálogo"
                  type="number"
                  min={0}
                />
                <FormTextField name="social_links.instagram" label="Instagram" type="url" />
                <FormTextField name="social_links.youtube" label="YouTube" type="url" />
                <FormTextField name="social_links.spotify" label="Spotify" type="url" />
                <FormTextField name="social_links.linkedin" label="LinkedIn" type="url" />
              </Grid>
              <FormTextField
                name="operation_description"
                label="Descrição da operação"
                inputElementType="textarea"
                rows={5}
                className="mt-24"
              />
            </Section>

            <Section title="Documentos e licenciamento">
              <Grid>
                <FormFileEntryField
                  name="validation_document_url"
                  label="Documento de validação"
                  diskPrefix="gad_partner_documents"
                  allowedFileTypes={['application/pdf', 'image/png', 'image/jpeg']}
                  showRemoveButton
                />
                <FormFileEntryField
                  name="contract_url"
                  label="Contrato/licenciamento"
                  diskPrefix="gad_partner_contracts"
                  allowedFileTypes={['application/pdf', 'image/png', 'image/jpeg']}
                  showRemoveButton
                />
              </Grid>
              <div className="mt-16 flex items-center gap-8 text-sm text-muted">
                <CloudUploadIcon size="sm" />
                <span>Arquivos ficam em storage privado e são revisados pelo admin.</span>
              </div>
            </Section>

            <Section title="Dados bancários, PIX e recebimento">
              <div className="mb-18">
                <SubsectionTitle title="Dados bancários" />
                <Grid>
                  <FormTextField name="future_bank_info.bank.bank_name" label="Nome do banco" />
                  <FormTextField name="future_bank_info.bank.bank_code" label="Código do banco (COMPE/ISPB)" />
                  <FormTextField name="future_bank_info.bank.branch_number" label="Agência" />
                  <FormTextField name="future_bank_info.bank.branch_digit" label="Dígito da agência" />
                  <FormTextField name="future_bank_info.bank.account_number" label="Número da conta" />
                  <FormTextField name="future_bank_info.bank.account_digit" label="Dígito da conta" />
                  <FormSelect
                    name="future_bank_info.bank.account_type"
                    selectionMode="single"
                    label="Tipo de conta"
                  >
                    <Item value="checking">Corrente</Item>
                    <Item value="savings">Poupança</Item>
                    <Item value="payment">Conta pagamento</Item>
                    <Item value="salary">Conta salário</Item>
                  </FormSelect>
                  <FormTextField name="future_bank_info.bank.beneficiary_name" label="Nome do favorecido" />
                  <FormTextField name="future_bank_info.bank.holder_tax_id" label="CPF/CNPJ do titular da conta" />
                </Grid>
              </div>

              <div className="mb-18">
                <SubsectionTitle title="Dados PIX" />
                <Grid>
                  <FormSelect
                    name="future_bank_info.pix.key_type"
                    selectionMode="single"
                    label="Tipo de chave PIX"
                  >
                    <Item value="cpf">CPF</Item>
                    <Item value="cnpj">CNPJ</Item>
                    <Item value="email">E-mail</Item>
                    <Item value="phone">Telefone</Item>
                    <Item value="random">Chave aleatória</Item>
                  </FormSelect>
                  <FormTextField name="future_bank_info.pix.key" label="Chave PIX" />
                  <FormTextField name="future_bank_info.pix.holder_name" label="Nome do titular PIX" />
                  <FormTextField name="future_bank_info.pix.bank_name" label="Banco vinculado ao PIX" />
                </Grid>
              </div>

              <SubsectionTitle title="Preferência de recebimento" />
              <Grid>
                <FormSelect
                  name="future_bank_info.payout.preferred_method"
                  selectionMode="single"
                  label="Método preferido de saque"
                >
                  <Item value="pix">PIX</Item>
                  <Item value="ted">TED</Item>
                </FormSelect>
                <FormTextField
                  name="future_bank_info.notes"
                  label="Observações financeiras"
                  inputElementType="textarea"
                  rows={3}
                />
              </Grid>
            </Section>

            <div className="rounded border bg-paper p-20">
              <label className="flex items-start gap-12 text-sm">
                <input
                  type="checkbox"
                  className="mt-4"
                  {...form.register('terms_accepted', {required: true})}
                />
                <span>
                  Aceito os termos de uso, política de integração, validação
                  documental e regras de responsabilidade sobre metadados,
                  licenciamento, ISRC, UPC/EAN e direitos autorais.
                </span>
              </label>
              {form.formState.errors.terms_accepted && (
                <div className="mt-8 text-sm text-danger">
                  O aceite dos termos é obrigatório.
                </div>
              )}
            </div>
              </div>
            </fieldset>

            <div className="flex flex-wrap items-center justify-between gap-16">
              <div className="flex items-center gap-8 text-sm text-muted">
                <CheckCircleIcon size="sm" className="text-positive" />
                <span>{existingAccount ? `Status atual: ${statusLabel(existingAccount.status)}` : 'Status inicial: Pending Review'}</span>
              </div>
              <Button
                type="submit"
                variant="flat"
                color="primary"
                disabled={formLocked || createAccount.isPending}
              >
                <Trans message={createAccount.isPending ? 'Enviando...' : existingAccount ? 'Reenviar cadastro GAD' : 'Enviar cadastro GAD'} />
              </Button>
            </div>
          </Form>
        </div>
      </main>
    </FileUploadProvider>
  );
}

function Section({title, children}: {title: string; children: React.ReactNode}) {
  return (
    <section className="rounded border bg-paper p-20 md:p-24">
      <h2 className="mb-20 text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function SubsectionTitle({title}: {title: string}) {
  return <h3 className="mb-14 text-sm font-semibold uppercase text-muted">{title}</h3>;
}

function Grid({children}: {children: React.ReactNode}) {
  return <div className="grid gap-20 md:grid-cols-2 xl:grid-cols-3">{children}</div>;
}

function emptyRegistrationForm(): PartnerRegistrationForm {
  return {
    company_type: 'label',
    country: 'Brasil',
    address: {country: 'Brasil'},
    social_links: {},
    future_bank_info: {},
    terms_accepted: false,
  } as PartnerRegistrationForm;
}

function formValuesFromAccount(account: ExistingPartnerAccount): PartnerRegistrationForm {
  const validationDocument = account.documents?.find(document => document.type === 'validation_document');
  const contract = account.documents?.find(document => document.type === 'contract_license');

  return {
    ...emptyRegistrationForm(),
    company_name: account.company_name,
    trade_name: account.trade_name,
    company_type: account.company_type,
    tax_id: account.tax_id,
    country: account.country || 'Brasil',
    address: account.address || {country: 'Brasil'},
    responsible_name: account.responsible_name,
    corporate_email: account.corporate_email,
    phone: account.phone,
    website: account.website,
    approximate_artists: account.approximate_artists,
    approximate_catalog: account.approximate_catalog,
    social_links: account.social_links || {},
    validation_document_url: validationDocument?.file_url || undefined,
    contract_url: contract?.file_url || undefined,
    operation_description: account.operation_description,
    future_bank_info: account.future_bank_info || {},
    terms_accepted: true,
  };
}

function statusLabel(status: string): string {
  return {
    pending_review: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
    suspended: 'Suspended',
    validation_required: 'Validation Required',
    blocked: 'Blocked',
  }[status] || status;
}
